import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Calendar, User, CheckCircle, DollarSign, FileText } from 'lucide-react';
import { PurchaseOrder } from '../../types';
import { purchaseOrderService } from '../../services/purchaseOrderService';
import { useTheme } from '../../contexts/ThemeContext';
import { useCurrency } from '../../hooks/useCurrency';

interface PurchaseOrderDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  purchaseOrder: PurchaseOrder;
  onSuccess: () => void;
}

const PurchaseOrderDetailsModal: React.FC<PurchaseOrderDetailsModalProps> = ({
  isOpen,
  onClose,
  purchaseOrder: initialPO
}) => {
  const { theme } = useTheme();
  const { formatCurrency } = useCurrency();
  const [po, setPO] = useState<PurchaseOrder>(initialPO);
  const [isLoading, setIsLoading] = useState(true);
  const [isClosing, setIsClosing] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsLoading(true);
      refreshPO();
    }
  }, [initialPO.id, isOpen]);

  const refreshPO = async () => {
    try {
      const data = await purchaseOrderService.getPurchaseOrder(initialPO.id);
      setPO(data);
    } catch (error) {
      console.error('Error refreshing purchase order:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false);
      onClose();
    }, 150);
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      pending: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
      received: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
      partial: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
      completed: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
      cancelled: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
    };
    return colors[status] || colors.pending;
  };

  const getPaymentStatusColor = () => {
    if (po.paid_amount === 0) {
      return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300';
    } else if (po.paid_amount >= po.total_amount) {
      return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
    } else {
      return 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300';
    }
  };

  const getPaymentStatusText = () => {
    if (po.paid_amount === 0) return 'Unpaid';
    if (po.paid_amount >= po.total_amount) return 'Paid';
    return 'Partial';
  };

  return createPortal(
    <div 
      className={`fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-2 sm:p-4 z-[9999] ${isClosing ? 'animate-fadeOut' : 'animate-fadeIn'}`}
      onClick={handleClose}
    >
      <div 
        className={`w-full max-w-5xl max-h-[90vh] flex flex-col rounded-xl shadow-2xl ${isClosing ? 'animate-scaleOut' : 'animate-scaleIn'} ${theme === 'dark' ? 'bg-slate-800' : 'bg-white'}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className={`px-4 sm:px-5 md:px-6 py-3 sm:py-4 border-b flex-shrink-0 rounded-t-xl ${theme === 'dark' ? 'border-slate-700 bg-slate-800/95' : 'border-slate-200 bg-gray-50/95'}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2 min-w-0 flex-1">
              <div className={`p-1.5 rounded-lg flex-shrink-0 ${theme === 'dark' ? 'bg-blue-900/50' : 'bg-blue-100'}`}>
                <FileText className={`w-3.5 h-3.5 sm:w-4 sm:h-4 md:w-5 md:h-5 ${theme === 'dark' ? 'text-blue-400' : 'text-blue-600'}`} />
              </div>
              <div className="min-w-0 flex-1">
                <h2 className={`text-sm sm:text-base md:text-lg font-semibold truncate ${theme === 'dark' ? 'text-white' : 'text-slate-900'}`}>
                  Purchase Order Details
                </h2>
                <p className={`text-xs truncate ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                  {po.po_number}
                </p>
              </div>
            </div>
            <button onClick={handleClose} className={`p-2 rounded-lg transition-colors touch-manipulation flex-shrink-0 ${theme === 'dark' ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'}`}>
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Scrollable Content */}
        <div className={`flex-1 overflow-y-auto ${theme === 'dark' ? 'bg-slate-800' : 'bg-white'}`}>
          {isLoading ? (
            // Loading Skeleton
            <div className="px-4 sm:px-5 md:px-6 py-4 sm:py-5 md:py-6 space-y-6">
              {/* Status Badges Skeleton */}
              <div className="flex gap-2">
                <div className={`h-6 w-24 rounded-full animate-pulse ${theme === 'dark' ? 'bg-slate-700' : 'bg-gray-200'}`} />
                <div className={`h-6 w-24 rounded-full animate-pulse ${theme === 'dark' ? 'bg-slate-700' : 'bg-gray-200'}`} />
              </div>

              {/* Info Cards Skeleton */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {[...Array(4)].map((_, idx) => (
                  <div key={idx} className={`p-4 rounded-lg ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-50'}`}>
                    <div className={`h-4 w-20 rounded-lg animate-pulse mb-2 ${theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'}`} />
                    <div className={`h-5 w-32 rounded-lg animate-pulse ${theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'}`} />
                  </div>
                ))}
              </div>

              {/* Items Table Skeleton */}
              <div>
                <div className={`h-4 w-24 rounded-lg animate-pulse mb-3 ${theme === 'dark' ? 'bg-slate-700' : 'bg-gray-200'}`} />
                <div className={`border rounded-lg overflow-hidden ${theme === 'dark' ? 'border-gray-700' : 'border-gray-300'}`}>
                  <div className={`p-4 ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-50'}`}>
                    <div className="space-y-2">
                      {[...Array(3)].map((_, idx) => (
                        <div key={idx} className={`h-10 w-full rounded-lg animate-pulse ${theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'}`} />
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Payment History Skeleton */}
              <div>
                <div className={`h-4 w-32 rounded-lg animate-pulse mb-3 ${theme === 'dark' ? 'bg-slate-700' : 'bg-gray-200'}`} />
                <div className={`border rounded-lg overflow-hidden ${theme === 'dark' ? 'border-gray-700' : 'border-gray-300'}`}>
                  <div className={`p-4 ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-50'}`}>
                    <div className="space-y-2">
                      {[...Array(2)].map((_, idx) => (
                        <div key={idx} className={`h-10 w-full rounded-lg animate-pulse ${theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'}`} />
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="px-4 sm:px-5 md:px-6 py-4 sm:py-5 md:py-6">
              {/* Status Badges */}
            <div className="flex gap-2 mb-6">
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(po.status)}`}>
                {po.status.charAt(0).toUpperCase() + po.status.slice(1)}
              </span>
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${getPaymentStatusColor()}`}>
                {getPaymentStatusText()}
              </span>
            </div>

            {/* Info Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <div className={`p-4 rounded-lg ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-50'}`}>
                <div className="flex items-center gap-2 mb-1">
                  <User className={`w-4 h-4 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`} />
                  <span className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>Supplier</span>
                </div>
                <p className={`font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  {po.supplier?.name}
                </p>
              </div>

              <div className={`p-4 rounded-lg ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-50'}`}>
                <div className="flex items-center gap-2 mb-1">
                  <Calendar className={`w-4 h-4 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`} />
                  <span className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>Purchase Date</span>
                </div>
                <p className={`font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  {new Date(po.purchase_date).toLocaleDateString()}
                </p>
              </div>

              <div className={`p-4 rounded-lg ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-50'}`}>
                <div className="flex items-center gap-2 mb-1">
                  <DollarSign className={`w-4 h-4 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`} />
                  <span className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>Total Amount</span>
                </div>
                <p className={`text-xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  {formatCurrency(po.total_amount)}
                </p>
              </div>

              <div className={`p-4 rounded-lg ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-50'}`}>
                <div className="flex items-center gap-2 mb-1">
                  <DollarSign className={`w-4 h-4 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`} />
                  <span className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>Due Amount</span>
                </div>
                <p className={`text-xl font-bold ${Number(po.due_amount) > 0 ? 'text-red-600' : 'text-green-600'}`}>
                  {formatCurrency(po.due_amount)}
                </p>
              </div>
            </div>

            {/* Items Table */}
            <div className="mb-6">
              <h4 className={`text-sm font-medium mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                Order Items
              </h4>
              <div className={`border rounded-lg overflow-hidden ${theme === 'dark' ? 'border-gray-700' : 'border-gray-300'}`}>
                <table className="w-full">
                  <thead className={theme === 'dark' ? 'bg-gray-700' : 'bg-gray-50'}>
                    <tr>
                      <th className={`px-4 py-2 text-left text-xs font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                        Product
                      </th>
                      <th className={`px-4 py-2 text-left text-xs font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                        Quantity
                      </th>
                      <th className={`px-4 py-2 text-left text-xs font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                        Received
                      </th>
                      <th className={`px-4 py-2 text-left text-xs font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                        Unit Price
                      </th>
                      <th className={`px-4 py-2 text-left text-xs font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                        Total
                      </th>
                    </tr>
                  </thead>
                  <tbody className={`divide-y ${theme === 'dark' ? 'divide-gray-700' : 'divide-gray-200'}`}>
                    {po.items.map((item) => (
                      <tr key={item.id}>
                        <td className={`px-4 py-2 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                          {item.product?.name || item.custom_product_name || 'Unknown Product'}
                        </td>
                        <td className={`px-4 py-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                          {item.quantity}
                        </td>
                        <td className={`px-4 py-2`}>
                          <span className={`flex items-center gap-1 ${
                            item.received_quantity >= item.quantity 
                              ? 'text-green-600' 
                              : 'text-orange-600'
                          }`}>
                            {item.received_quantity >= item.quantity && <CheckCircle className="w-4 h-4" />}
                            {item.received_quantity}
                          </span>
                        </td>
                        <td className={`px-4 py-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                          {formatCurrency(item.unit_price)}
                        </td>
                        <td className={`px-4 py-2 font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                          {formatCurrency(item.total_price)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Payment History */}
            {po.payments && po.payments.length > 0 && (
              <div className="mb-6">
                <h4 className={`text-sm font-medium mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                  Payment History
                </h4>
                <div className={`border rounded-lg overflow-hidden ${theme === 'dark' ? 'border-gray-700' : 'border-gray-300'}`}>
                  <table className="w-full">
                    <thead className={theme === 'dark' ? 'bg-gray-700' : 'bg-gray-50'}>
                      <tr>
                        <th className={`px-4 py-2 text-left text-xs font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                          Date
                        </th>
                        <th className={`px-4 py-2 text-left text-xs font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                          Amount
                        </th>
                        <th className={`px-4 py-2 text-left text-xs font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                          Method
                        </th>
                        <th className={`px-4 py-2 text-left text-xs font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                          Reference
                        </th>
                      </tr>
                    </thead>
                    <tbody className={`divide-y ${theme === 'dark' ? 'divide-gray-700' : 'divide-gray-200'}`}>
                      {po.payments.map((payment) => (
                        <tr key={payment.id}>
                          <td className={`px-4 py-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                            {new Date(payment.payment_date).toLocaleDateString()}
                          </td>
                          <td className={`px-4 py-2 font-medium text-green-600`}>
                            {formatCurrency(payment.amount)}
                          </td>
                          <td className={`px-4 py-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                            {payment.payment_method}
                          </td>
                          <td className={`px-4 py-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                            {payment.reference_number || '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Notes */}
            {po.notes && (
              <div>
                <h4 className={`text-sm font-medium mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                  Notes
                </h4>
                <p className={`p-3 rounded-lg text-sm ${theme === 'dark' ? 'bg-gray-700 text-gray-300' : 'bg-gray-50 text-gray-700'}`}>
                  {po.notes}
                </p>
              </div>
            )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className={`px-4 sm:px-5 md:px-6 py-3 sm:py-4 border-t flex-shrink-0 rounded-b-xl ${theme === 'dark' ? 'border-slate-700 bg-slate-800/95' : 'border-slate-200 bg-gray-50/95'}`}>
          <div className="flex justify-end">
            <button 
              onClick={handleClose} 
              className={`px-4 py-2.5 text-sm font-medium rounded-xl border transition-all duration-200 touch-manipulation ${theme === 'dark' ? 'border-gray-600 text-gray-300 hover:bg-gray-700 hover:border-gray-500' : 'border-gray-300 text-gray-700 hover:bg-gray-100'}`}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default PurchaseOrderDetailsModal;
